import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PointsIcon from '@wix/da-ds/pkg/Icons/Points';
import ProgressBar from '@wix/da-shared-react/pkg/ProgressBar';
import Flex from '@wix/da-ds/pkg/Flex';
import classnames from 'classnames';
import { PapiEditorText } from '@wix/da-papi-types';
import TextRenderer from '@wix/da-shared-react/pkg/TextRenderer';
import DonationViewer from '@wix/da-shared-react/pkg/DaEditorWrapper/Viewer/Donation';
import { isEditorTextEmpty } from '@wix/da-shared-react/pkg/DaEditorWrapper/utils';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import TextTooltipOnHover from '@wix/da-shared-react/pkg/Popover/Preset/TextTooltipOnHover';
import { BiEventType } from '@wix/da-bi/pkg/constants';
import BiLink from '@wix/da-bi/pkg/Components/BiLink';
import DonatePointsPopup from './DonatePointsPopup';

import s from './Donations.scss';

export interface Props {
  amount: number;
  className?: string;
  description?: string | PapiEditorText;
  donors: number;
  goal: number;
  isOwner: boolean;
  moduleId: number;
  moduleVersion: number;
  pushDonateModal: (username: string, title: string) => void;
  title?: string;
  userUuid: string;
  username: string;
}

export const Donations: React.FC<Props> = ({
  amount,
  className,
  description,
  donors,
  goal,
  isOwner,
  moduleId,
  moduleVersion,
  pushDonateModal,
  title,
  userUuid,
  username,
}) => {
  const { t } = useTranslation();
  const shouldUseTheNewPopup = useFlag('donate_points_popup');
  const [showDonatePointsPopup, setShowDonatePointsPopup] = useState(false);
  const hideDonatePointsPopup = useCallback(() => {
    setShowDonatePointsPopup(false);
  }, []);

  const handleDonatePointsClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      e.preventDefault();
      if (isOwner) {
        return;
      }
      if (shouldUseTheNewPopup) {
        setShowDonatePointsPopup(true);
        return;
      }
      pushDonateModal(username, t('widgets.donations.donateModal.title'));
    },
    [isOwner, username, pushDonateModal, shouldUseTheNewPopup, t]
  );

  let hasDescription = false;
  if (description) {
    hasDescription =
      typeof description === 'string'
        ? description.length > 0
        : !isEditorTextEmpty(description);
  }
  const percentage = goal ? (amount / goal) * 100 : 0;
  const hasReachedGoal = amount >= goal;
  return (
    <>
      <div
        className={classnames(
          s['root'],
          !hasDescription && s['no-description'],
          className
        )}
      >
        <div className={s['wrapper']}>
          {/* title and description */}
          {title && <div className={s['title']}>{title}</div>}
          {hasDescription && (
            <div className={s['description']}>
              {typeof description === 'string' ? (
                { description }
              ) : (
                <TextRenderer
                  textContent={description}
                  reactViewer={DonationViewer}
                  legacyTheme={'default'}
                  viewerKey={`donation-${moduleId}-${moduleVersion}`}
                />
              )}
            </div>
          )}

          {/* goal */}
          <Flex className={s['section-title']} alignItems="center">
            <span>{goal}</span>
            <PointsIcon className={s['points-icon']} />
          </Flex>
          <div className={s['section-description']}>
            {t('widgets.donations.goal')}
            {isOwner && !goal && (
              <span className={s['empty-goal']}>
                {t('widgets.donations.emptyGoal')}
              </span>
            )}
          </div>

          {/* donors */}
          <div className={s['section-title']}>{donors}</div>
          <div className={s['section-description']}>
            {t('widgets.donations.donors')}
          </div>

          {/* status */}
          <div className={s['section-title']}>
            {t('widgets.donations.status')}
          </div>
          <div
            className={classnames(
              s['section-description'],
              s['section-description-status']
            )}
          >
            {t('widgets.donations.status.progress', {
              progress: `${amount}/${goal}`,
            })}
          </div>

          <ProgressBar
            percentage={percentage}
            className={s['progressbar-root']}
          />

          {hasReachedGoal && (
            <div className={s['goal-reached']}>
              {t('widgets.donations.reached.thanks')}{' '}
              <span className={s['accent']}>
                {t('widgets.donations.reached.helped')}
              </span>
            </div>
          )}
        </div>
      </div>
      <div className={s['footer']}>
        {isOwner ? (
          <TextTooltipOnHover
            tooltipText={t('widgets.donations.button.tooltip')}
            renderReference={({ ref }) => (
              <BiLink
                onClick={handleDonatePointsClick}
                className={classnames(
                  s['donate-button'],
                  isOwner && s['disabled']
                )}
                biData={{
                  evid: BiEventType.DONATE_POINTS_CLICK,
                  recipientid: userUuid,
                }}
              >
                <span ref={ref}>{t('widgets.donations.button.donate')}</span>
              </BiLink>
            )}
          />
        ) : (
          <BiLink
            onClick={handleDonatePointsClick}
            className={classnames(s['donate-button'], isOwner && s['disabled'])}
            biData={{
              evid: BiEventType.DONATE_POINTS_CLICK,
              recipientid: userUuid,
            }}
          >
            <span>{t('widgets.donations.button.donate')}</span>
          </BiLink>
        )}
      </div>
      {showDonatePointsPopup && (
        <DonatePointsPopup
          username={username}
          moduleId={moduleId}
          close={hideDonatePointsPopup}
        />
      )}
    </>
  );
};

export default Donations;
